'use client'

import { useCallback, useState, useEffect, useRef } from 'react'
import { CopilotFeedbackScreen } from './copilot-feedback-screen'
import { ResizablePanel } from './resizable'
import { useMotionValue, useMotionValueEvent, motion, AnimatePresence } from 'framer-motion'
import { useCopilot } from '@/providers/copilot-provider'
import { Sparkles, Play, Bot } from 'lucide-react'
import { useAuth } from '@/contexts/auth-context'
import { Button } from '@/components/ui/button'
import { useFeedback } from '@/hooks/use-feedback'
import { useAIBot } from '@/hooks/use-ai-bot'
import { SessionType, type BotMessage } from '@/types/ai-bot-framework'

import {
  ChatMessageList,
  ChatInput,
  buildChatMessage,
  appendToMessage,
  generateMessageId,
} from '@/components/chat'
import type { ChatMessageData, MessageStatus } from '@/components/chat'

export function CopilotChat() {
  // ---------------------------------------------------------------------------
  // Context & Auth
  // ---------------------------------------------------------------------------
  const { isVisible, copilotMode, setCopilotMode } = useCopilot()
  const { context } = useFeedback()
  const auth = useAuth()

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------
  const [messages, setMessages] = useState<ChatMessageData[]>([])
  const [input, setInput] = useState('')
  const [autoSubmit, setAutoSubmit] = useState(false)
  const [isBotSelectorOpen, setIsBotSelectorOpen] = useState(false)
  const [toolCallCount, setToolCallCount] = useState(0)
  const [currentToolCalls, setCurrentToolCalls] = useState<string[]>([])
  const [thinkingMessageId, setThinkingMessageId] = useState<string | null>(null)
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null)

  const width = useMotionValue(400)
  const formRef = useRef<HTMLDivElement>(null)

  // Use a ref for thinkingMessageId inside the onMessage callback
  // to avoid recreating the callback on every state change (fixes line-rewrite bug)
  const thinkingMessageIdRef = useRef<string | null>(null)
  useEffect(() => {
    thinkingMessageIdRef.current = thinkingMessageId
  }, [thinkingMessageId])

  // ---------------------------------------------------------------------------
  // SignalR
  // ---------------------------------------------------------------------------
  const {
    sessionId: _sessionId,
    isConnected,
    isRegistered,
    currentBot,
    availableBots,
    isProcessing,
    sendMessage: sendBotMessage,
    respondToElement,
    switchBot,
  } = useAIBot({
    requestedBotId: 'qa-form-builder',
    sessionType: SessionType.Visible,
    onMessage: useCallback((message: BotMessage) => {
      const messageId = message.requestId

      // Self-describing field checks:
      // Status-only → thinking indicator, no chat bubble
      const isStatusUpdate = !!message.status && !message.content && !message.interactiveElements && !message.visualization
      const isCompletionMessage = message.isComplete

      // Parse tool calls from status field
      if (message.status?.tool) {
        const toolName = message.status.tool.name
        if (message.status.tool.phase === 'calling') {
          setCurrentToolCalls(prev => [...prev, toolName])
          setToolCallCount(prev => prev + 1)
        }
      }

      setMessages(prev => {
        const existingIndex = prev.findIndex(msg => msg.id === messageId)

        if (existingIndex === -1) {
          const newContent = message.content || ''
          const newMsg = buildChatMessage({
            id: messageId,
            role: 'assistant',
            content: newContent,
            timestamp: new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            status: (isStatusUpdate && !isCompletionMessage) ? 'thinking' : 'streaming',
            visualization: message.visualization,
            interactiveElements: message.interactiveElements,
          })
          if (isStatusUpdate && !isCompletionMessage) {
            setThinkingMessageId(messageId)
          }
          return [...prev, newMsg]
        }

        return prev.map(msg => {
          if (msg.id !== messageId) return msg

          let newStatus: MessageStatus
          if (msg.status === 'error') {
            newStatus = 'error'
          } else if (isStatusUpdate && !isCompletionMessage) {
            newStatus = 'thinking'
            setThinkingMessageId(messageId)
          } else {
            newStatus = message.isComplete ? 'complete' : 'streaming'
            if ((message.content || message.isComplete) && thinkingMessageIdRef.current === messageId) {
              setThinkingMessageId(null)
              setToolCallCount(0)
              setCurrentToolCalls([])
            }
          }

          return appendToMessage(
            msg,
            {
              content: message.content,
              visualization: message.visualization,
              interactiveElements: message.interactiveElements,
            },
            { status: newStatus },
          )
        })
      })
    }, []), // Empty deps — uses ref for thinkingMessageId
  })

  // ---------------------------------------------------------------------------
  // User actions
  // ---------------------------------------------------------------------------
  const handleStartSession = useCallback(async () => {
    if (isProcessing || !isRegistered) return
    const userName = auth?.user?.name || 'User'
    const pageName = window.location.pathname.split('/').filter(Boolean).pop() || 'Home'
    const initMessage = `Hello, my name is ${userName}. I am on the ${pageName} page and I would like to start a session with you.`
    try {
      await sendBotMessage(initMessage, { isHiddenInit: true })
    } catch (err) {
      console.error('Failed to start session:', err)
      setMessages(prev => [
        ...prev,
        buildChatMessage({
          id: generateMessageId(),
          role: 'assistant',
          content: 'Sorry, something went wrong. Please try again.',
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          status: 'error',
        }),
      ])
    }
  }, [auth, isProcessing, isRegistered, sendBotMessage])

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      const currentInput = input.trim()
      if (!currentInput || isProcessing || !isRegistered) return

      setMessages(prev => [
        ...prev,
        buildChatMessage({
          id: generateMessageId(),
          role: 'user',
          content: currentInput,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          status: 'complete',
        }),
      ])
      setInput('')
      setAutoSubmit(false)

      try {
        await sendBotMessage(currentInput)
      } catch (err) {
        console.error('Failed to send message:', err)
        setMessages(prev => [
          ...prev,
          buildChatMessage({
            id: generateMessageId(),
            role: 'assistant',
            content: 'Sorry, something went wrong. Please try again.',
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            status: 'error',
          }),
        ])
      }
    },
    [input, isProcessing, isRegistered, sendBotMessage],
  )

  // Auto-submit
  useEffect(() => {
    if (autoSubmit && input && !isProcessing && isRegistered) {
      handleSubmit({ preventDefault: () => {} } as React.FormEvent)
    }
  }, [input, autoSubmit, handleSubmit, isProcessing, isRegistered])

  // External input event
  useEffect(() => {
    const handleSetInput = (e: CustomEvent<{ text: string }>) => {
      setInput(e.detail.text)
      setAutoSubmit(true)
    }
    window.addEventListener('set-copilot-input', handleSetInput as EventListener)
    return () => window.removeEventListener('set-copilot-input', handleSetInput as EventListener)
  }, [])

  // ---------------------------------------------------------------------------
  // Interactive element handlers (wired to ChatMessageList)
  // ---------------------------------------------------------------------------
  const handleRespondToElement = useCallback(async (elementId: string, actionMetadata: Record<string, unknown>) => {
    await respondToElement(elementId, actionMetadata)
  }, [respondToElement])

  const handleSendMessage = useCallback(async (content: string) => {
    await sendBotMessage(content)
  }, [sendBotMessage])

  // ---------------------------------------------------------------------------
  // Copy handler
  // ---------------------------------------------------------------------------
  const handleCopy = useCallback((messageId: string, _content: string) => {
    setCopiedMessageId(messageId)
    setTimeout(() => setCopiedMessageId(null), 2000)
  }, [])

  // ---------------------------------------------------------------------------
  // Width CSS variable
  // ---------------------------------------------------------------------------
  useEffect(() => {
    document.documentElement.style.setProperty('--copilot-width', isVisible ? `${width.get()}px` : '0px')
  }, [width, isVisible])

  useMotionValueEvent(width, 'change', latest => {
    if (isVisible) {
      document.documentElement.style.setProperty('--copilot-width', `${latest}px`)
    }
  })

  // ---------------------------------------------------------------------------
  // Filtered messages (hide init messages)
  // ---------------------------------------------------------------------------
  const visibleMessages = messages.filter(msg => {
    if (msg.metadata?.isHiddenInit) return false
    return (msg.rawContent.trim().length > 0) || thinkingMessageId === msg.id || (msg.interactiveElements && msg.interactiveElements.length > 0) || !!msg.visualization
  })

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="fixed right-0 top-[64px] bottom-0 z-50"
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'spring', bounce: 0, duration: 0.3 }}
        >
          <ResizablePanel
            defaultSize={400}
            minSize={300}
            maxSize={800}
            side="left"
            className="h-full border-l border-border bg-background"
            motionValue={width}
          >
            <div className="flex h-full flex-col">
              {/* ---- Header ---- */}
              <div className="border-b border-border p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Sparkles className="size-4 text-primary" />
                    <span className="font-medium text-sm">AI Assistant</span>
                  </div>
                  {!isConnected && <span className="text-xs text-muted-foreground">Connecting...</span>}
                  {isConnected && !isRegistered && <span className="text-xs text-muted-foreground">Initializing...</span>}
                  {availableBots.length > 1 && isRegistered && currentBot && (
                    <>
                      {!isBotSelectorOpen ? (
                        <button
                          type="button"
                          className="rounded px-2 py-1 text-xs font-medium text-foreground hover:underline"
                          onClick={() => !isProcessing && setIsBotSelectorOpen(true)}
                        >
                          {currentBot.botName}
                        </button>
                      ) : (
                        <select
                          value={currentBot.botId}
                          onChange={e => {
                            switchBot(e.target.value)
                            setMessages([])
                            setIsBotSelectorOpen(false)
                          }}
                          onBlur={() => setIsBotSelectorOpen(false)}
                          autoFocus
                          className="rounded border border-border bg-background px-2 py-1 text-xs text-foreground"
                          disabled={isProcessing}
                        >
                          {availableBots.map(bot => (
                            <option key={bot.botId} value={bot.botId}>
                              {bot.botName}
                            </option>
                          ))}
                        </select>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* ---- Messages ---- */}
              <div className="flex-1 overflow-y-auto px-4 py-4">
                {copilotMode === 'chat' && (
                  <>
                    {visibleMessages.length === 0 && !isProcessing && isRegistered && (
                      <div className="py-8 text-center text-muted-foreground">
                        <Bot className="mx-auto mb-2 size-8 opacity-50" />
                        <p className="mb-4">Start a conversation with the AI assistant</p>
                        <Button onClick={handleStartSession} variant="outline" size="sm" className="gap-2">
                          <Play className="size-4" />
                          Start Session
                        </Button>
                      </div>
                    )}

                    {visibleMessages.length > 0 && (
                      <ChatMessageList
                        messages={visibleMessages}
                        thinkingMessageId={thinkingMessageId}
                        toolCallCount={toolCallCount}
                        currentToolCalls={currentToolCalls}
                        onCopyMessage={handleCopy}
                        copiedMessageId={copiedMessageId}
                        isStreaming={isProcessing}
                        onRespondToElement={handleRespondToElement}
                        onSendMessage={handleSendMessage}
                      />
                    )}
                  </>
                )}
                {copilotMode === 'feedback' && (
                  <CopilotFeedbackScreen
                    onClose={() => setCopilotMode('chat')}
                    itemName={context?.itemName ?? ''}
                  />
                )}
              </div>

              {/* ---- Input ---- */}
              {copilotMode === 'chat' && (
                <div className="border-t border-border p-3">
                  <div ref={formRef}>
                    <ChatInput
                      value={input}
                      onChange={setInput}
                      onSubmit={handleSubmit}
                      isProcessing={isProcessing}
                      isRegistered={isRegistered}
                      compact
                    />
                  </div>
                </div>
              )}
            </div>
          </ResizablePanel>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
